/**
 * Customer Settings V2 — "Third Party" section: native V2 rebuild of
 * the legacy `CustomerThirdPartyTabOld` dual-list vendor-access
 * editor.
 *
 * Contains:
 * - ThirdPartySection (default export, forwardRef)
 * - ThirdPartySectionRow — row renderer for the MultiSelect panes
 */

import HubIcon from '@mui/icons-material/Hub';
import { Box, Checkbox, FormControlLabel } from '@mui/material';
import { yellow } from '@mui/material/colors';
import { useFormikContext } from 'formik';
import { Map as ImmutableMap, List as ImmutableList, Set as ImmutableSet } from 'immutable';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import MultiSelect from '@sophocles/components/MultiSelect';
import { fetchAllPayingCustomers, thirdPartyMinimalCustomerSelector } from '@sophocles/entities/Customer';
import { fetchThirdPartyCustomerRelationshipForCustomer } from '@sophocles/entities/ThirdPartyCustomerRelationship';
import { useBulkEditThirdPartiesMutation } from '@sophocles/features/entities/customers';
import { SettingsSection } from '@sophocles/views/settings/components';

import { staffOnlyChip } from './chips';

// Native V2 rebuild of the legacy `CustomerThirdPartyTabOld` tab.
// V1 wrapped MultiSelect inside its own `ContentSection` chrome; once
// V2 mounts that inside a `SettingsSection`, the user sees a doubled
// card-in-card layout with two sets of titles + descriptions.  Drop
// the inner ContentSection by inlining the legacy state / handlers
// here so the SettingsSection chrome is the only one — same dual-list
// interaction model, same fetch + bulk-edit dispatch.
//
// The section carries no local Save button.  Dirtiness is signalled
// up to the page-level Formik via the `__pending_third_party_dirty`
// synthetic field, and the unified Save bar drives submission through
// the imperative `submit()` handle (CustomerSettingsViewV2.handleSave)
// when the section is dirty.  The bulk-edit hits its own endpoint,
// not the sectioned settings PATCH, so it is folded in as a side
// dispatch rather than a section in the PATCH body.
//
// The "Only show digital vendors" filter — V1 buried it inside the
// ContentSection header — is lifted to the section header `action`
// slot so it sits in the same line as the title.
//
// NOTE: this section still uses Immutable collections (List / Map / Set
// and ``row.get('id')`` accessors) because the shared MultiSelect
// component in ``components/MultiSelect/`` is built on Immutable —
// it reads ``.size`` and ``.get()`` directly.  Cleaning up to plain
// JS here requires modernizing MultiSelect first; tracked as a
// separate concern.
export function ThirdPartySectionRow({ row, t }) {
  const id = row.get('id', 0);
  const name = row.get('name', 'none?');
  const isDigital = row.get('sells_digital_panels', false);
  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        width: '100%',
      }}
    >
      <Box
        component="span"
        sx={{ flexGrow: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        title={`${id}. ${name}`}
      >
        {id}. {name}
      </Box>
      {isDigital ? (
        <Box>
          <i
            title={t('general.digitalVendor')}
            className="fa fa-desktop"
            style={{ color: yellow[700], flexShrink: 0 }}
          />
        </Box>
      ) : null}
    </Box>
  );
}

const ThirdPartySection = React.forwardRef(function ThirdPartySection({ customer, onTablet }, ref) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { setFieldValue } = useFormikContext();
  const [bulkEditThirdParties] = useBulkEditThirdPartiesMutation();
  const minimalCustomers = useSelector(thirdPartyMinimalCustomerSelector);

  const customerId = customer.get('id', 0);
  const [selected, setSelected] = React.useState(ImmutableSet());
  const [initial, setInitial] = React.useState(ImmutableSet());
  const [onlyDigital, setOnlyDigital] = React.useState(false);

  // Load the paging customer list + the existing third-party
  // relations once on mount.  Same flow as V1's componentDidMount.
  React.useEffect(() => {
    dispatch(fetchAllPayingCustomers());
    dispatch(fetchThirdPartyCustomerRelationshipForCustomer(customerId)).then(data => {
      if (!data || data.error) return;
      const ids = ImmutableMap(data)
        .map(item => item.id)
        .toSet();
      setSelected(ids);
      setInitial(ids);
    });
  }, [dispatch, customerId]);

  const sorted = (minimalCustomers || ImmutableList())
    .sortBy(item => item.get('id'))
    .filter(item => item.get('id') !== customerId && (!onlyDigital || item.get('sells_digital_panels', false)));
  const selectedData = sorted.filter(item => selected.includes(item.get('id', 0)));
  const unSelectedData = sorted.filterNot(item => selected.includes(item.get('id', 0)));

  // Dirtiness lives in the page-level Formik so the unified Save bar
  // reflects this section — push a boolean whenever the selection
  // diverges from what loaded.
  const markDirty = next => setFieldValue('__pending_third_party_dirty', !next.equals(initial), false);
  const handleSelect = item => {
    const next = selected.add(item.get('id', 0));
    setSelected(next);
    markDirty(next);
  };
  const handleUnSelect = item => {
    const next = selected.remove(item.get('id', 0));
    setSelected(next);
    markDirty(next);
  };

  // Imperative submit fired by the page-level Save bar.  Preserves
  // the V1 bulk-edit payload byte-for-byte; throws on error so the
  // unified save surfaces it and leaves the form dirty for retry.
  const submit = React.useCallback(async () => {
    const formatted = (minimalCustomers || ImmutableList())
      .filter(item => selected.includes(item.get('id', 0)))
      .map(item => ({
        third_party_id: item.get('id'),
        create_one_site_group_for_entire_customer: false,
        third_party_can_relocate: true,
      }))
      .toArray();
    const result = await bulkEditThirdParties({ customerId, body: formatted });
    if (result.error) {
      throw new Error(_.get(result.error?.data?.errors, 'third_party_id', String(result.error)));
    }
    const ids = ImmutableMap(result.data)
      .map(item => item.id)
      .toSet();
    setSelected(ids);
    setInitial(ids);
    setFieldValue('__pending_third_party_dirty', false, false);
  }, [minimalCustomers, selected, customerId, bulkEditThirdParties, setFieldValue]);

  React.useImperativeHandle(ref, () => ({ submit }), [submit]);

  const digitalFilter = (
    <FormControlLabel
      label={t('general.onlyShowDigitalVendors')}
      control={<Checkbox size="small" checked={onlyDigital} onChange={() => setOnlyDigital(v => !v)} />}
      sx={{ m: 0 }}
    />
  );

  return (
    <SettingsSection
      id="settings-third-party"
      icon={<HubIcon />}
      title={t('customer.3rdParty', 'Third Party')}
      description={t(
        'customerSettings.thirdPartyDescription',
        'Configure the third-party vendors this customer grants access to.',
      )}
      chip={staffOnlyChip(t)}
      action={digitalFilter}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <MultiSelect
          selectedData={selectedData}
          unSelectedData={unSelectedData}
          onSelectData={handleSelect}
          onUnSelectData={handleUnSelect}
          selectedTitle={t('customerSettings.v2.allowedThirdParties', 'Allowed Third Parties')}
          unSelectedTitle={t('customerSettings.v2.notAllowedThirdParties', 'Not Allowed Third Parties')}
          rowGenerator={datum => <ThirdPartySectionRow row={datum} t={t} />}
          onTablet={onTablet}
        />
      </Box>
    </SettingsSection>
  );
});

export default ThirdPartySection;
